import { c as _c } from "react/compiler-runtime";
import type { MarketId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { type ApiInputToken, parseApiAsset, toBigInt, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useMemo } from 'react';
import { formatUnits } from 'viem';
import type { Warning } from '@/components/common/WarningIcon';
import type { MarketListItem } from '@/lib/procedures/marketList.contract';
import { getMarketWithPoints, NO_LEND_POINTS } from '@/components/charts/helpers/getPoints';
import { computeRewards } from '@/helpers/rewards';
import { getMarketWarningMessage } from '@/helpers/warningMessages';
import { apiWarningLevelToEnum, isApiWarningLevel } from '@/helpers/warnings';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import type { TableMarketRow } from '../config/TableMarket.types';
import { userMarketPositionKey } from '../config/config';
type MarketAsset = MarketListItem['loanAsset'];
const toApiInputToken = (asset: MarketAsset, chainId: number): ApiInputToken => ({
  address: asset.address,
  symbol: asset.symbol,
  name: asset.name ?? undefined,
  decimals: asset.decimals,
  logoURI: asset.logoUri,
  priceUsd: asset.priceUsd,
  chain: {
    id: chainId
  }
});
export const useTableMarketRow = t0 => {
  const $ = _c(8);
  const {
    markets,
    userMarketPositionKeys
  } = t0;
  const {
    showPoints
  } = useFeatureFlags();
  let t1;
  bb0: {
    if (!markets) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== markets || $[2] !== showPoints || $[3] !== userMarketPositionKeys) {
      let t3;
      if ($[5] !== showPoints || $[6] !== userMarketPositionKeys) {
        t3 = market => {
          const loanAsset = parseApiAsset(toApiInputToken(market.loanAsset, market.chainId));
          const collateralAsset = market.collateralAsset ? parseApiAsset(toApiInputToken(market.collateralAsset, market.chainId)) : null;
          const marketId = market.marketId as MarketId;
          const rewards = market.borrowRewards.map(_temp).filter(_temp2).map(_temp3);
          const supplyRewards = market.supplyRewards.map(_temp4).filter(_temp5);
          const warnings = [];
          for (const warning of market.warnings) {
            if (!isApiWarningLevel(warning.level)) {
              continue;
            }
            const message = getMarketWarningMessage({
              type: warning.type,
              level: warning.level,
              metadata: {
                content: warning.content,
                badDebtAssets: warning.badDebtAssets,
                badDebtUsd: warning.badDebtUsd
              }
            });
            if (!message) {
              continue;
            }
            warnings.push({
              level: apiWarningLevelToEnum(warning.level),
              message
            });
          }
          const avgBorrowApy = isDefined(market.avgBorrowApy) ? toBigInt(safeParseNumber(market.avgBorrowApy)) : null;
          const avgNetBorrowApy = isDefined(market.avgNetBorrowApy) ? toBigInt(safeParseNumber(market.avgNetBorrowApy)) : null;
          const avgSupplyApy = isDefined(market.avgSupplyApy) ? toBigInt(safeParseNumber(market.avgSupplyApy)) : null;
          const avgNetSupplyApy = isDefined(market.avgNetSupplyApy) ? toBigInt(safeParseNumber(market.avgNetSupplyApy)) : null;
          const netBorrowApy = isDefined(market.netBorrowApy) ? safeParseNumber(market.netBorrowApy) : null;
          const borrowApy = isDefined(market.borrowApy) ? safeParseNumber(market.borrowApy) : null;
          const weeklyNetBorrowApy = isDefined(market.weeklyNetBorrowApy) ? safeParseNumber(market.weeklyNetBorrowApy) : null;
          const weeklyBorrowApy = isDefined(market.weeklyBorrowApy) ? safeParseNumber(market.weeklyBorrowApy) : null;
          const points = showPoints ? getMarketWithPoints(market.chainId, marketId, collateralAsset?.address).filter(isDefined) : [];
          const hasUserPosition = userMarketPositionKeys.has(userMarketPositionKey(market.chainId, marketId));
          const borrowToTarget = isDefined(market.borrowToTargetUsd) ? safeParseNumber(market.borrowToTargetUsd) : undefined;
          return {
            network: {
              chainId: market.chainId
            },
            market: {
              marketId,
              chainId: market.chainId,
              collateralAsset,
              loanAsset,
              hasUserPosition,
              warnings,
              collateralNativeYield: market.collateralYield,
              collateralRewards: market.collateralRewards.map(_temp6)
            },
            lltv: Number(formatUnits(BigInt(market.lltv), 18)),
            sizeUsd: {
              amount: isDefined(market.size) ? toBigInt(market.size) : null,
              valueUsd: isDefined(market.sizeUsd) ? safeParseNumber(market.sizeUsd) : null
            },
            totalLiquidityUsd: {
              amount: isDefined(market.totalLiquidity) ? toBigInt(market.totalLiquidity) : null,
              valueUsd: isDefined(market.totalLiquidityUsd) ? safeParseNumber(market.totalLiquidityUsd) : null
            },
            avgNetBorrowApy: {
              avgNetBorrowApy,
              avgBorrowApy,
              rewards,
              points,
              asset: collateralAsset
            },
            netSupplyApy: {
              netSupplyApy: avgNetSupplyApy,
              supplyApy: avgSupplyApy,
              rewards: supplyRewards,
              points: NO_LEND_POINTS,
              asset: loanAsset
            },
            trustedBy: {
              curators: market.curators
            },
            utilization: market.utilization,
            liquidityAssets: {
              amount: isDefined(market.liquidityAssets) ? toBigInt(market.liquidityAssets) : null,
              valueUsd: isDefined(market.liquidityAssetsUsd) ? safeParseNumber(market.liquidityAssetsUsd) : null,
              chainId: market.chainId,
              marketId
            },
            collateralAssetsUsd: {
              amount: isDefined(market.collateralAssets) ? toBigInt(market.collateralAssets) : null,
              valueUsd: isDefined(market.collateralAssetsUsd) ? safeParseNumber(market.collateralAssetsUsd) : null
            },
            netBorrowApy: {
              netBorrowApy,
              borrowApy,
              rewards,
              points,
              asset: collateralAsset
            },
            weeklyBorrowApy: {
              weeklyNetBorrowApy,
              weeklyBorrowApy,
              rewards,
              points,
              asset: collateralAsset
            },
            supplyAssetsUsd: {
              amount: isDefined(market.supplyAssets) ? toBigInt(market.supplyAssets) : null,
              valueUsd: isDefined(market.supplyAssetsUsd) ? safeParseNumber(market.supplyAssetsUsd) : null
            },
            borrowAssetsUsd: {
              amount: isDefined(market.borrowAssets) ? toBigInt(market.borrowAssets) : null,
              valueUsd: isDefined(market.borrowAssetsUsd) ? safeParseNumber(market.borrowAssetsUsd) : null
            },
            oraclePrice: {
              price: isDefined(market.oraclePrice) ? toBigInt(market.oraclePrice) : null,
              collateralDecimals: market.collateralAsset?.decimals ?? null,
              loanDecimals: market.loanAsset.decimals,
              loanSymbol: market.loanAsset.symbol
            },
            irmAddress: market.irmAddress as Address,
            borrowToTarget,
            apyAtTarget: market.apyAtTarget,
            badDebt: {
              underlying: toBigInt(market.badDebt.underlying),
              usd: isDefined(market.badDebt.usd) ? safeParseNumber(market.badDebt.usd) : null
            },
            realizedBadDebt: {
              underlying: toBigInt(market.realizedBadDebt.underlying),
              usd: isDefined(market.realizedBadDebt.usd) ? safeParseNumber(market.realizedBadDebt.usd) : null
            }
          };
        };
        $[5] = showPoints;
        $[6] = userMarketPositionKeys;
        $[7] = t3;
      } else {
        t3 = $[7];
      }
      t2 = markets.map(t3).filter(isDefined);
      $[1] = markets;
      $[2] = showPoints;
      $[3] = userMarketPositionKeys;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t2;
  }
  return t1;
};
function _temp(reward) {
  return computeRewards({
    rewardToken: {
      ...reward.asset,
      logoURI: reward.asset.logoUri,
      chain: {
        id: reward.asset.chainId
      }
    },
    apr: reward.borrowApr
  });
}
function _temp2(reward_0) {
  return reward_0.apr && reward_0.apr > ZERO;
}
function _temp3(reward_1) {
  return {
    ...reward_1,
    apr: isDefined(reward_1.apr) ? -reward_1.apr : null
  };
}
function _temp4(reward_2) {
  return computeRewards({
    rewardToken: {
      ...reward_2.asset,
      logoURI: reward_2.asset.logoUri,
      chain: {
        id: reward_2.asset.chainId
      }
    },
    apr: reward_2.supplyApr
  });
}
function _temp5(reward_3) {
  return reward_3.apr && reward_3.apr > ZERO;
}
function _temp6(reward_4) {
  return {
    asset: {
      ...reward_4.asset,
      logoURI: reward_4.asset.logoUri
    },
    collateralApr: reward_4.collateralApr
  };
}